import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { spring } from "../lib/springs";

/* ============================================================================
   Banner — a status message, hand-ported from Fluid Functionalism's Banner
   (fluidfunctionalism.com/docs/banner). Same API, so the real component can
   replace it once the app moves to Tailwind v4 + shadcn:

     <Banner status="info" contrast="high" dismissible onDismiss={...}>
       <BannerTitle>…</BannerTitle>
       <BannerDescription>…</BannerDescription>
       <BannerActions>
         <BannerAction variant="primary" onClick={...}>Open</BannerAction>
       </BannerActions>
     </Banner>

   status    default | info | success | warning | error
   contrast  low  — neutral fill, only the glyph carries the colour
             high — the whole banner in a 12% wash of it (8% for default)
   variant   inline | fixed — fixed is a sticky full-bleed bar at the top of
             its scroll container that pushes the content down
   open      controls visibility; omitted, the ✕ hides it on its own

   Layout and colours are the `.bn-*` block in theme.css: one 4-column grid
   (glyph, text, actions, ✕). Actions trail a title-only banner and drop under
   the text once there is a description. Appearing springs in on the slow
   tier with a slight scale; dismissing fades on the quicker exit tween while
   the row collapses, so nothing below jumps. A banner open on first render
   is simply there. Errors and warnings are role="alert", the rest "status".
   ========================================================================== */

const Glyph = ({ status }) => {
  if (status === "warning") {
    return (
      <svg viewBox="0 0 20 20" className="bn-glyph" aria-hidden="true">
        <path d="M10 2.5 18.5 17.5h-17z" className="bn-glyph-fill" strokeLinejoin="round" />
        <path d="M10 8v4M10 14.6v.1" className="bn-glyph-mark" />
      </svg>
    );
  }
  if (status === "default") {
    return (
      <svg viewBox="0 0 20 20" className="bn-glyph bn-glyph-outline" aria-hidden="true">
        <circle cx="10" cy="10" r="7.5" />
        <path d="M10 9v4.5M10 6.6v.1" />
      </svg>
    );
  }
  const mark = {
    info: "M10 9v4.5M10 6.6v.1",
    success: "M6.8 10.2l2.2 2.2 4.2-4.6",
    error: "M7.5 7.5l5 5M12.5 7.5l-5 5",
  }[status];
  return (
    <svg viewBox="0 0 20 20" className="bn-glyph" aria-hidden="true">
      <circle cx="10" cy="10" r="8" className="bn-glyph-fill" />
      <path d={mark} className="bn-glyph-mark" />
    </svg>
  );
};

export const Banner = ({
  status = "default",
  contrast = "low",
  variant = "inline",
  icon: Icon,
  dismissible = false,
  open,
  onDismiss,
  className = "",
  children,
}) => {
  const [shownInternal, setShownInternal] = useState(true);
  const shown = open ?? shownInternal;

  const dismiss = () => {
    if (open === undefined) setShownInternal(false);
    onDismiss?.();
  };

  return (
    <AnimatePresence initial={false}>
      {shown ? (
        <motion.div
          key="banner-row"
          className={"bn-row bn-row-" + variant}
          initial={{ height: 0 }}
          animate={{ height: "auto", transition: spring.slow }}
          exit={{ height: 0, transition: spring.slow.exit }}
        >
          <motion.div
            className={[
              "bn",
              "bn-" + status,
              "bn-contrast-" + contrast,
              "bn-" + variant,
              className,
            ].join(" ")}
            role={status === "error" || status === "warning" ? "alert" : "status"}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1, transition: spring.slow }}
            exit={{ opacity: 0, scale: 0.98, transition: spring.slow.exit }}
          >
            <span className="bn-icon">
              {Icon ? <Icon className="bn-glyph bn-glyph-outline" aria-hidden="true" /> : <Glyph status={status} />}
            </span>
            {children}
            {dismissible ? (
              <button type="button" className="bn-close" aria-label="Dismiss" onClick={dismiss}>
                <svg viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M6 6l8 8M14 6l-8 8" />
                </svg>
              </button>
            ) : null}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export const BannerTitle = ({ children, className = "" }) => (
  <p className={("bn-title " + className).trim()}>{children}</p>
);

export const BannerDescription = ({ children, className = "" }) => (
  <p className={("bn-desc " + className).trim()}>{children}</p>
);

export const BannerActions = ({ children, className = "" }) => (
  <div className={("bn-actions " + className).trim()}>{children}</div>
);

/* variant: primary | secondary | ghost. Each orders itself with CSS `order`
   (the strongest at the edge), so write primary first for tab order. */
export const BannerAction = ({
  variant = "secondary",
  href,
  external = false,
  className = "",
  children,
  ...props
}) => {
  const cls = ["bn-action", "bn-action-" + variant, className].join(" ").trim();
  if (href) {
    return (
      <a
        href={href}
        className={cls}
        {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
        {...props}
      >
        {children}
      </a>
    );
  }
  return (
    <button type="button" className={cls} {...props}>
      {children}
    </button>
  );
};

export default Banner;
